import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Map, MessageSquare, Star, ArrowRight, 
  Clock, Mail, Eye,
  TrendingUp, Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, enquiriesResponse] = await Promise.all([
        adminApi.getStats(),
        adminApi.getEnquiries({ limit: 5 })
      ]);
      setStats(statsResponse.data);
      // enquiriesResponse.data contains { data: [...], total, page, ... }
      setRecentEnquiries(enquiriesResponse.data.data || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      await adminApi.seedData();
      toast.success('Demo data loaded successfully!');
      loadDashboardData();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to load demo data');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      contacted: 'bg-blue-100 text-blue-700 border-blue-200',
      converted: 'bg-green-100 text-green-700 border-green-200',
      closed: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return (
      <Badge variant="outline" className={cn('capitalize', styles[status] || styles.pending)}>
        {status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const statCards = [
    { 
      title: 'Total Packages', 
      value: stats?.tours?.total || 0, 
      subtitle: `${stats?.tours?.active || 0} active`,
      icon: Package, 
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      link: '/admin/tours'
    },
    { 
      title: 'Total Enquiries', 
      value: stats?.enquiries?.total || 0, 
      subtitle: `${stats?.enquiries?.pending || 0} pending`,
      icon: MessageSquare, 
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      link: '/admin/enquiries'
    },
    { 
      title: 'Pending Enquiries', 
      value: stats?.enquiries?.pending || 0, 
      subtitle: 'Need attention',
      icon: Clock, 
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      link: '/admin/enquiries?status=pending'
    },
    { 
      title: 'Testimonials', 
      value: stats?.testimonials?.total || 0, 
      subtitle: `${stats?.testimonials?.pending || 0} pending review`,
      icon: Star, 
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      link: '/admin/testimonials'
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Dashboard
          </h1>
          <p className="text-[#4A4A4A] mt-1">Welcome back! Here&apos;s an overview of your business.</p>
        </div>
        {stats?.tours?.total === 0 && (
          <Button 
            onClick={handleSeedData} 
            className="bg-gradient-to-r from-[#FF6B35] to-[#E85A2D] hover:from-[#E85A2D] hover:to-[#D4491D] shadow-lg"
            data-testid="seed-data-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Load Demo Data
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        {statCards.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card 
              className="hover:shadow-lg transition-all duration-300 cursor-pointer border-0 overflow-hidden group"
              data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#4A4A4A] mb-1">{stat.title}</p>
                    <p className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]">
                      {loading ? (
                        <span className="inline-block w-12 h-8 bg-gray-200 rounded animate-pulse" />
                      ) : (
                        stat.value
                      )}
                    </p>
                    <p className="text-sm text-[#4A4A4A] mt-1 flex items-center gap-1">
                      {stat.subtitle}
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                  <div className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br shadow-lg",
                    stat.color
                  )}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Enquiries Table */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                Recent Enquiries
              </CardTitle>
              <Link to="/admin/enquiries">
                <Button variant="ghost" size="sm" className="text-[#FF6B35] hover:text-[#E85A2D] hover:bg-[#FF6B35]/10">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl animate-pulse">
                      <div className="w-10 h-10 bg-gray-200 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentEnquiries.length === 0 ? (
                <div className="text-center py-12 text-[#4A4A4A]">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No enquiries yet</p>
                  <p className="text-sm mt-1">Enquiries will appear here when customers submit them</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="recent-enquiries-table">
                    <thead>
                      <tr className="text-left text-sm text-[#4A4A4A] border-b">
                        <th className="pb-3 font-medium">Customer</th>
                        <th className="pb-3 font-medium hidden md:table-cell">Package</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium hidden sm:table-cell">Date</th>
                        <th className="pb-3 font-medium text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {recentEnquiries.map((enquiry) => (
                        <tr key={enquiry.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4">
                            <div>
                              <p className="font-medium text-[#1A1A1A]">{enquiry.name}</p>
                              <p className="text-sm text-[#4A4A4A] flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {enquiry.email}
                              </p>
                            </div>
                          </td>
                          <td className="py-4 hidden md:table-cell">
                            <p className="text-sm text-[#4A4A4A] truncate max-w-[150px]">
                              {enquiry.package_name || 'General Enquiry'}
                            </p>
                          </td>
                          <td className="py-4">
                            {getStatusBadge(enquiry.status)}
                          </td>
                          <td className="py-4 hidden sm:table-cell">
                            <p className="text-sm text-[#4A4A4A]">
                              {formatDate(enquiry.created_at)}
                            </p>
                          </td>
                          <td className="py-4 text-right">
                            <Link to={`/admin/enquiries?id=${enquiry.id}`}>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Eye className="w-4 h-4 text-[#4A4A4A]" />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions & Activity */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link 
                to="/admin/tours" 
                className="flex items-center justify-between p-4 bg-gradient-to-r from-[#FF6B35]/5 to-[#FF6B35]/10 rounded-xl hover:from-[#FF6B35]/10 hover:to-[#FF6B35]/20 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B35] rounded-xl flex items-center justify-center">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-[#1A1A1A]">Manage Packages</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#FF6B35] group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link 
                to="/admin/enquiries?status=pending" 
                className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl hover:bg-[#FF6B35]/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-[#1A1A1A]">Pending Enquiries</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#4A4A4A] group-hover:translate-x-1 group-hover:text-[#FF6B35] transition-all" />
              </Link>

              <Link 
                to="/admin/testimonials" 
                className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl hover:bg-[#FF6B35]/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-[#1A1A1A]">Testimonials</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#4A4A4A] group-hover:translate-x-1 group-hover:text-[#FF6B35] transition-all" />
              </Link>
              
              <Link 
                to="/admin/settings" 
                className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl hover:bg-[#FF6B35]/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#004E64] rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-[#1A1A1A]">Site Settings</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#4A4A4A] group-hover:translate-x-1 group-hover:text-[#FF6B35] transition-all" />
              </Link>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats?.enquiries?.pending > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageSquare className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A] text-sm">
                        {stats.enquiries.pending} pending enquiries
                      </p>
                      <p className="text-xs text-[#4A4A4A]">Awaiting your response</p>
                    </div>
                  </div>
                )}
                
                {stats?.testimonials?.pending > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Star className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A] text-sm">
                        {stats.testimonials.pending} testimonials pending
                      </p>
                      <p className="text-xs text-[#4A4A4A]">Review and approve</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Package className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A] text-sm">
                      {stats?.tours?.active || 0} active packages
                    </p>
                    <p className="text-xs text-[#4A4A4A]">Currently available for booking</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
